import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../hooks/useSocket.js';

import { fetchParticipant } from '../api/fetchParticipant.js';
import * as currentUserUtils from '../utils/currentUserUtils.js';
import ProfileImage from './ProfileImage.js';
import { Tooltip } from 'antd';


import 'dayjs/locale/is.js';
dayjs.extend(relativeTime);

// Utility function to calculate age from Icelandic kennitala (SSN)
const calculateAgeFromKennitala = (kennitala) => {
    if (!kennitala || kennitala.length < 6) return null;

    // Icelandic kennitala format: DDMMYY-XXXX
    const day = parseInt(kennitala.substring(0, 2), 10);
    const month = parseInt(kennitala.substring(2, 4), 10);
    let year = parseInt(kennitala.substring(4, 6), 10);

    // Determine century (people born after 2000 have different century digit)
    const centuryDigit = kennitala.length >= 10 ? kennitala[9] : '0';
    if (centuryDigit === '9' || centuryDigit === '0') {
        year += 1900;
    } else {
        year += 2000;
    }

    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
};

// Determine age group
const getAgeGroup = (age) => {
    if (age === null || age < 20) return null;
    if (age >= 60) return '60+';
    if (age >= 50) return '50+';
    if (age >= 40) return '40+';
    if (age >= 30) return '30+';
    if (age >= 20) return '20+';
    return null;
};

// Age group badge component
const AgeGroupBadge = ({ ageGroup, showAgegroup = true }) => {
    if (!ageGroup || !showAgegroup) return null;

    const colors = {
        '20+': { bg: '#FCE4EC', text: '#C2185B' }, // Soft pink
        '30+': { bg: '#E8F5E9', text: '#2E7D32' }, // Soft green
        '40+': { bg: '#E3F2FD', text: '#1565C0' }, // Soft blue
        '50+': { bg: '#FFF3E0', text: '#E65100' }, // Soft orange
        '60+': { bg: '#F3E5F5', text: '#6A1B9A' }, // Soft purple
    };

    const style = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2px 6px',
        borderRadius: '20%',
        backgroundColor: colors[ageGroup]?.bg || '#f0f0f0',
        color: colors[ageGroup]?.text || '#666',
        fontSize: '9px',
        fontWeight: '600',
        marginRight: '4px',
      
    };

    return (
        <Tooltip title={`Age group: ${ageGroup}`}>
            <div style={style}>{ageGroup}</div>
        </Tooltip>
    );
};

export default function Participation({
    participant,
    onChange,
    currentParticipantId,
    ownerId,
    eventId,
    sessionId,
    showAgegroup,
}) {
    const socket = useSocket(eventId, sessionId);
    const emitAttendanceUpdate = socket?.emitAttendanceUpdate;
    const onAttendanceChanged = socket?.onAttendanceChanged;
    const isConnected = socket?.isConnected;
    let me = false;
    const [showProfile, setShowProfile] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Listen for real-time attendance updates from other users
    useEffect(() => {
        if (!onAttendanceChanged) return;

        let lastUpdateTimestamp = 0;

        const cleanup = onAttendanceChanged((data) => {
            // Only process if this is the exact participant that was updated
            if (data.participantId === participant.participant_id) {
                // Deduplicate rapid updates (within 100ms)
                const updateTime = new Date(data.timestamp).getTime();
                if (updateTime - lastUpdateTimestamp < 100) {
                    console.log('Skipping duplicate update within 100ms');
                    return;
                }
                lastUpdateTimestamp = updateTime;

                // Create a synthetic event that will trigger the parent's onChange
                // This updates the parent's participants state properly
                const syntheticEvent = {
                    target: {
                        name: participant.attendance_id,
                        value: data.status === 'yes' ? '1' : '0'
                    },
                    // Mark this as a sync update to avoid API call
                    isUpdateFromSync: true
                };

                // Call the parent's onChange handler to update the state
                if (onChange) {
                    onChange(syntheticEvent);
                }
            }
        });
        return cleanup;
    }, [onAttendanceChanged, participant.participant_id, participant.attendance_id, participant.name, onChange]);

    const [user_name, setParticipantNameUser] = useState('');
    const [user_nickname, setParticipantNickNameUser] = useState('');
    const [user_email, setParticipantEmailUser] = useState('');
    const [user_phone, setParticipantPhoneUser] = useState('');
    const [user_team, setParticipantTeamUser] = useState('');
    const [me_forced, setMeForced] = useState(true);
    const [user_ssn, setParticipantSSNUser] = useState('');
    //const [user_active, setParticipantActiveUser] = useState('');
    //const [user_id, setParticipantidUser] = useState('');

    const isGuest = participant.is_guest === true;
    const rowId = participant.participant_id || participant.attendance_id;

    const { t } = useTranslation();

    const handleAttendanceChange = async (event) => {

        setIsUpdating(true);
        try {
            // Emit socket update for real-time
            if (eventId && sessionId) {
                const socketConnected = isConnected();

                if (socketConnected) {
                    const status = event.target.value === '1' ? 'yes' : 'no';
                    emitAttendanceUpdate({
                        eventId,
                        sessionId,
                        participantId: participant.participant_id,
                        status: status,
                        participantName: participant.name
                    });

                    // Mark the event to indicate it came from socket update
                    event.isFromSocketUpdate = true;
                }
            }

            // Call traditional API
            if (onChange) {
                await onChange(event);
            }
        } catch (error) {
            console.error('Error updating attendance:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const getParticipant = async (id) => {
        try {
            const res = await fetchParticipant(id);
            setParticipantNameUser(res.name);
            setParticipantNickNameUser(res.nickname);
            setParticipantEmailUser(res.email);
            setParticipantPhoneUser(res.mobile_number);
            setParticipantTeamUser(res.Lið);
            setParticipantSSNUser(res.ssn);
            //setParticipantActiveUser(res.Virkur);
            //setParticipantidUser(res.id);
        } catch (err) {
            console.error(err);
        }
    };

    // Show delete participant confirmation modal
    const showParticipantProfile = (id) => {
        if (showProfile) {
            setShowProfile(!showProfile);
        } else {
            getParticipant(id);
            setShowProfile(!showProfile);
        }
    };

    const updaterNameToShow = (participant) => {
        if (participant.updater === null) { return ''; }
        if (participant.updated_by === participant.participant_id) { return ''; }
        const index = participant.updater.name.indexOf(' ');
        const updaterName =
            index > 0
                ? participant.updater.name.substring(0, index)
                : participant.updater.name;
        return (
            ' (' +
            updaterName +
            (participant.updater.nickname !== ''
                ? ' / ' + participant.updater.nickname
                : '') +
            ')'
        );
    };

    const removeMe = () => {
        currentUserUtils.deleteParticipantForOwner(ownerId);
        setMeForced(false);
    };

    if (participant.participant_id === currentParticipantId) {
        me = true;
    }

    // Calculate age and age group from SSN
    // SSN can be at participant.ssn or participant.participant.ssn depending on the data structure
    const ssn = participant.ssn || participant.participant?.ssn;
    const age = ssn ? calculateAgeFromKennitala(ssn) : null;
    const ageGroup = getAgeGroup(age);

    // Determine background color based on age group
    const getRowBackgroundColor = () => {
        if (me === true && me_forced === true) return undefined; // Let the 'me' class handle it
        if (!ageGroup || !showAgegroup) return undefined; // Don't show background if toggle is off

        const bgColors = {
            '20+': 'rgba(252, 228, 236, 0.5)', // Soft pink
            '30+': 'rgba(232, 245, 233, 0.5)', // Soft green
            '40+': 'rgba(227, 242, 253, 0.5)', // Soft blue
            '50+': 'rgba(255, 243, 224, 0.5)', // Soft orange
            '60+': 'rgba(243, 229, 245, 0.5)', // Soft purple
        };

        return bgColors[ageGroup];
    };

    return (
        <div
            className={`columns playa ${
                participant.confirmed === true ? 'active' : ''
            } ${me === true && me_forced === true ? 'me' : ''}`}
            style={{ backgroundColor: getRowBackgroundColor() }}
        >
            {me && me_forced === true && (
                <div className="me-icon">
                    {t('Me')}
                    <div className="not-me" onClick={removeMe}>
                        This is not me
                    </div>
                </div>
            )}
            <div className="column is-one-half name">
                {participant.confirmed === true && (
                    <img src="/img/check.svg" alt={t('Is participating')} />
                )}
                {participant.confirmed === false && (
                    <img
                        src="/img/nocheck.svg"
                        alt={t('Is not participating')}
                    />
                )}
                {isGuest && (
                    <Tooltip title={t('Guest')}>
                        <img
                            src="/img/icon-guest.svg"
                            alt={t('Guest')}
                            title={t('Guest')}
                            className="guest-icon"
                        />
                    </Tooltip>
                )}
                <AgeGroupBadge ageGroup={ageGroup} showAgegroup={showAgegroup} />
                <span
                    id={`name-${rowId}`}
                    className={!isGuest ? 'profile-link' : ''}
                    onClick={
                        !isGuest
                            ? () =>
                                  showParticipantProfile(
                                      participant.participant_id
                                  )
                            : undefined
                    }
                    style={{ cursor: !isGuest ? 'pointer' : 'default' }}
                >
                    {participant.name}
                </span>

                {participant.nickname && <> / {participant.nickname}</>}
            </div>
            <div className="column is-one-quarter radio">
                <div className="radio-custom">
                    <input
                        type="radio"
                        id={'yes-' + rowId}
                        name={participant.attendance_id}
                        value="1"
                        checked={participant.confirmed === true}
                        onChange={handleAttendanceChange}
                        disabled={isUpdating}
                    />
                    <label htmlFor={'yes-' + rowId} className="radio-label">
                        {t('Yes')}
                    </label>
                </div>

                <div className="radio-custom">
                    <input
                        type="radio"
                        id={'no-' + rowId}
                        name={participant.attendance_id}
                        value="0"
                        checked={participant.confirmed === false}
                        onChange={handleAttendanceChange}
                        disabled={isUpdating}
                    />
                    <label htmlFor={'no-' + rowId} className="radio-label no">
                        {t('No')}
                    </label>
                </div>
            </div>
            <div
                className="column is-one-quarter date"
                id={'timestamp-' + participant.attendance_id}
            >
                {(participant.confirmed === null
                    ? ''
                    : dayjs(participant.timestamp).fromNow()) +
                    updaterNameToShow(participant)}
            </div>
            {showProfile && (
                <div className="modal is-active">
                    <div className="profile-card">
                        <div className="algn">
                            <div className="card">
                                <div
                                    className="close-btn"
                                    onClick={showParticipantProfile}
                                >
                                    <img src="/img/close.png" alt="Close" />
                                </div>
                                <div className="upper-bg">
                                    <ProfileImage
                                        participant={participant.participant}
                                        className="profile-pic"
                                    />
                                </div>
                                <div className="lower-bg">
                                    <div className="text">
                                        <p className="name">{user_name}</p>
                                        <p className="title">
                                            {user_nickname} <br /> {user_team}{' '}
                                            <br /> {user_email} <br />{' '}
                                            {user_phone}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
